import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

/**
 * A research request summary represents a summary of content related to a research request,
 * which can be associated with a query, hypothesis, or evidence.
 */
export const ResearchRequestSummarySchema = z.object({
  researchRequestSummaryId: PrimaryKeySchema,
  researchRequestId: PrimaryKeySchema,
  researchRequestQueryId: PrimaryKeySchema.nullable(),
  researchRequestHypothesisId: PrimaryKeySchema.nullable(),
  researchRequestEvidenceId: PrimaryKeySchema.nullable(),
  contentHash: z.string(),
  summary: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type ResearchRequestSummary = z.infer<
  typeof ResearchRequestSummarySchema
>;

export const ResearchRequestSummariesSchema = z.array(
  ResearchRequestSummarySchema,
);

export type ResearchRequestSummaries = z.infer<
  typeof ResearchRequestSummariesSchema
>;

export const ResearchRequestSummariesBaseQuerySchema = z.object({
  researchRequestSummaryIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  researchRequestIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
  researchRequestQueryIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  researchRequestHypothesisIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  researchRequestEvidenceIds: commaDelimitedList(
    PrimaryKeySchema.array(),
  ).optional(),
  contentHashes: commaDelimitedList(z.string().array()).optional(),
  researchRequestQueryIsNull: z.coerce.boolean().optional(),
  researchRequestHypothesisIsNull: z.coerce.boolean().optional(),
  researchRequestEvidenceIsNull: z.coerce.boolean().optional(),
});

export const ResearchRequestSummariesQuerySchema =
  createOffsetPaginationRequestQuerySchema(
    z.enum(['createdAt', 'updatedAt']),
  ).merge(ResearchRequestSummariesBaseQuerySchema);

export type ResearchRequestSummariesQuery = z.infer<
  typeof ResearchRequestSummariesQuerySchema
>;

export const CreateResearchRequestSummarySchema =
  ResearchRequestSummarySchema.pick({
    researchRequestId: true,
    researchRequestQueryId: true,
    researchRequestHypothesisId: true,
    researchRequestEvidenceId: true,
    contentHash: true,
    summary: true,
  });

export type CreateResearchRequestSummary = z.infer<
  typeof CreateResearchRequestSummarySchema
>;
